import { Link } from "react-router-dom"
import { Button } from "./ui/button"
import { Gift, LayoutDashboard, Shield, Book, Hammer, Moon, Sun, Menu, X } from "lucide-react"
import { useEffect, useState } from "react"

const Navbar = () => {
    const auth = window.localStorage.getItem('auth')
    const user = auth ? JSON.parse(auth).user : null
    const [isDark, setIsDark] = useState(false)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

    useEffect(() => {
        // Check for saved theme preference or default to light mode
        const savedTheme = localStorage.getItem('theme')
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        
        if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
            setIsDark(true)
            document.documentElement.classList.add('dark')
        }
    }, [])

    const toggleTheme = () => {
        if (isDark) {
            document.documentElement.classList.remove('dark')
            localStorage.setItem('theme', 'light')
            setIsDark(false)
        } else {
            document.documentElement.classList.add('dark')
            localStorage.setItem('theme', 'dark')
            setIsDark(true)
        }
    }

    const handleLogout = () => {
        window.localStorage.removeItem('auth')
        window.location.href = '/'
    }

    const closeMobileMenu = () => {
        setMobileMenuOpen(false)
    }

    return (
        <nav className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container mx-auto px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 md:gap-6">
                        <Link to="/" className="text-xl font-bold flex items-center gap-2">
                            FOSSIT
                        </Link>
                        {auth && (
                            <div className="hidden md:flex items-center gap-4">
                                <Link to="/dashboard">
                                    <Button variant="ghost" size="sm" className="gap-2">
                                        <LayoutDashboard className="h-4 w-4" />
                                        Dashboard
                                    </Button>
                                </Link>
                                <Link to="/store">
                                    <Button variant="ghost" size="sm" className="gap-2">
                                        <Gift className="h-4 w-4" />
                                        Store
                                    </Button>
                                </Link>
                                <Link to="/contributions">
                                    <Button variant="ghost" size="sm" className="gap-2">
                                        <Hammer className="h-4 w-4" />
                                        Contributions
                                    </Button>
                                </Link>
                                <Link to="/resources">
                                    <Button variant="ghost" size="sm" className="gap-2">
                                        <Book className="h-4 w-4" />
                                        Resources
                                    </Button>
                                </Link>
                                {user?.isAdmin && (
                                    <Link to="/admin">
                                        <Button variant="ghost" size="sm" className="gap-2">
                                            <Shield className="h-4 w-4" />
                                            Admin Panel
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 md:gap-4">
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={toggleTheme}
                            className="gap-2"
                            aria-label="Toggle theme"
                        >
                            {isDark ? (
                                <Sun className="h-5 w-5" />
                            ) : (
                                <Moon className="h-5 w-5" />
                            )}
                        </Button>
                        {auth && (
                            <>
                                {user && (
                                    <div className="hidden md:flex items-center gap-2">
                                        <img 
                                            src={user.avatarUrl} 
                                            alt={user.username}
                                            className="h-8 w-8 rounded-full"
                                        />
                                        <span className="text-sm font-medium">{user.username}</span>
                                    </div>
                                )}
                                <Button variant="outline" size="sm" onClick={handleLogout} className="hidden sm:inline-flex">
                                    Logout
                                </Button>
                                {/* Mobile menu button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="md:hidden"
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                                    aria-expanded={mobileMenuOpen}
                                >
                                    {mobileMenuOpen ? (
                                        <X className="h-5 w-5" />
                                    ) : (
                                        <Menu className="h-5 w-5" />
                                    )}
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Mobile Navigation Menu */}
                {auth && mobileMenuOpen && (
                    <div className="md:hidden border-t mt-3 pt-3 pb-2">
                        <div className="flex flex-col gap-2">
                            {user && (
                                <div className="flex items-center gap-2 px-2 py-2 border-b pb-3 mb-2">
                                    <img 
                                        src={user.avatarUrl} 
                                        alt={user.username}
                                        className="h-8 w-8 rounded-full"
                                    />
                                    <span className="text-sm font-medium">{user.username}</span>
                                </div>
                            )}
                            <Link to="/dashboard" onClick={closeMobileMenu}>
                                <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                                    <LayoutDashboard className="h-4 w-4" />
                                    Dashboard
                                </Button>
                            </Link>
                            <Link to="/store" onClick={closeMobileMenu}>
                                <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                                    <Gift className="h-4 w-4" />
                                    Store
                                </Button>
                            </Link>
                            <Link to="/contributions" onClick={closeMobileMenu}>
                                <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                                    <Hammer className="h-4 w-4" />
                                    Contributions
                                </Button>
                            </Link>
                            <Link to="/resources" onClick={closeMobileMenu}>
                                <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                                    <Book className="h-4 w-4" />
                                    Resources
                                </Button>
                            </Link>
                            {user?.isAdmin && (
                                <Link to="/admin" onClick={closeMobileMenu}>
                                    <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                                        <Shield className="h-4 w-4" />
                                        Admin Panel
                                    </Button>
                                </Link>
                            )}
                            <div className="border-t pt-2 mt-2">
                                <Button variant="outline" size="sm" onClick={handleLogout} className="w-full">
                                    Logout
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </nav>
    )
}

export {
    Navbar,
}